import Api from './Api';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason } from './FormPrimitives';

/**
 * v0.9.0 — 检查排课弹窗 (辉哥 10/7 拍板「固定课模式右键菜单加检查排课」)
 *
 * ## 这个弹窗为什么重要
 *   物化器遇到冲突会「跳过不生成」(调研报告 §3.2 的自愈设计)。跳过在课表上的表现
 *   就是「这周少了一节课」—— 用户分不清是本来就没排, 还是被冲突吃掉了。
 *   没有这个弹窗, **漏排是不可见的**。它是验证前五块对不对的唯一手段。
 *
 * ## 四类 + 两种一键修复 (哪些给一键、哪些不给, 是刻意的)
 *   漏排         → 给「一键补排」   (纯技术问题, 补上就行)
 *   因卡过期停排 → 给「一键恢复」   (卡已续费的话, 恢复后排课自动接上)
 *   被单次改过   → **不给**修复按钮 (这是用户的明确决定, 自动改回去等于撤销用户操作)
 *   孤儿课次     → **不给**修复按钮 (模板已删, 该恢复模板还是删课次是业务决策)
 *   强行给个「一键全修」, 迟早有人点下去把用户请假的课改回来。
 *
 * 纯 DOM 实现 (Flarum 2.0 的 `m` 是 Preact, m.render() 不可用),
 * 照 SlotContextMenu.js 的模式自绘。
 */
const EXIT_MS = 130;
const ZSFXCheck = {
  _root: null,
  _detachFns: [],
  _ensureRoot() {
    if (this._root && document.body.contains(this._root)) return this._root;
    const r = document.createElement('div');
    r.className = 'ZS-ctxmenu-root';
    document.body.appendChild(r);
    this._root = r;
    return r;
  },
  _destroyRoot() {
    if (this._root && this._root.parentNode) this._root.parentNode.removeChild(this._root);
    this._root = null;
  },
  _detachAll() {
    const fns = this._detachFns || [];
    for (let i = 0; i < fns.length; i++) {
      try {
        fns[i]();
      } catch (e) {/* noop */}
    }
    this._detachFns = [];
  },
  close() {
    this._detachAll();
    const root = this._root;
    if (!root) return;
    root.classList.remove('ZS-ctxmenu--in');
    root.classList.add('ZS-ctxmenu--out');
    setTimeout(() => {
      if (this._root === root) {
        root.classList.remove('ZS-ctxmenu--out');
        this._destroyRoot();
      }
    }, EXIT_MS);
  },
  /**
   * @param {object} opts
   * @param {number} opts.schoolId
   * @param {Function} opts.onRefresh 修复完成后重载课表
   */
  async open(opts) {
    this.close();
    this._detachAll();
    const root = this._ensureRoot();
    const state = {
      loading: true,
      data: null,
      errorMsg: '',
      fixing: ''
    };
    const scrim = document.createElement('div');
    scrim.className = 'ZS-ctxmenu-scrim ZS-ctxmenu-scrim--dialog';
    root.appendChild(scrim);
    const dlg = document.createElement('div');
    dlg.className = 'ZS-fxmodal ZS-fxcheck';
    root.appendChild(dlg);
    root.classList.add('ZS-ctxmenu--in');
    const close = () => this.close();
    scrim.addEventListener('click', close);
    const offKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', offKey, true);
    this._detachFns.push(() => document.removeEventListener('keydown', offKey, true));
    const render = () => {
      dlg.innerHTML = '';
      this._render(dlg, state, opts, render, close);
    };
    render();
    try {
      state.data = await Api.checkFixedLessons(opts.schoolId);
      state.errorMsg = '';
    } catch (e) {
      // v0.17.66 — 为什么不能只看 `e.message` (改前这里只有 `|| '网络错误'`)
      //   后端抛 ValidationException 时 HTTP 是 422, **真实原因在
      //   `e.response.errors[0].detail`**; `e.message` 只是请求层拼的
      //   "Response code 422 (Unprocessable Entity)" (或干脆为空), 跟后端那句提示无关。
      //   后果: 「无权检查」/「校区 id 缺失」这类**可操作**的原因被吞成一句「网络错误」,
      //   用户会以为是网断了去反复重试, 而真实原因是权限或参数。
      //   形状照抄 v0.17.65 统一后的写法; 兜底文案「网络错误」原文保留, 降为最后一档。
      state.errorMsg = '检查失败: ' + errReason(e, '网络错误');
    }
    state.loading = false;
    render();
  },
  _render(dlg, state, opts, render, close) {
    const head = document.createElement('div');
    head.className = 'ZS-fxmodal-head';
    head.innerHTML = '<div class="ZS-fxmodal-hdicon"><i class="fas fa-clipboard-check"></i></div>' + '<div><div class="ZS-fxmodal-hdt">检查排课</div>' + '<div class="ZS-fxmodal-hds" data-role="win"></div></div>';
    dlg.appendChild(head);
    const body = document.createElement('div');
    body.className = 'ZS-fxmodal-body';
    dlg.appendChild(body);
    if (state.loading) {
      body.innerHTML = '<div class="ZS-fxmodal-loading">正在扫描本月 + 下月排课…</div>';
      return;
    }
    if (state.errorMsg) {
      body.innerHTML = '<div class="ZS-fxmodal-error">' + this._esc(state.errorMsg) + '</div>';
      return;
    }
    const d = state.data || {};
    const s = d.summary || {};
    const win = d.window || {};
    const headS = head.querySelector('[data-role="win"]');
    if (headS) headS.textContent = '扫描范围 ' + (win.from || '?') + ' ~ ' + (win.to || '?');

    // ── 顶部汇总数字 ────────────────────────────────────────────────
    const sum = document.createElement('div');
    sum.className = 'ZS-fxcheck-sum';
    sum.innerHTML = this._sumCell('漏排', s.missing || 0, s.missing ? 'is-bad' : 'is-ok') + this._sumCell('因卡过期停排', s.paused || 0, s.paused ? 'is-warn' : 'is-ok') + this._sumCell('被单次改过', s.overridden || 0, 'is-info') + this._sumCell('孤儿课次', s.orphan || 0, s.orphan ? 'is-warn' : 'is-ok') +
    // v0.17.59 — 「课槽已有课」被拒的课次。与「漏排」分开: 漏排是「还没排上, 补排就能好」,
    //   这里是「明确被规则拒绝, 重试多少次都排不上」, 必须让用户一眼看见原因。
    this._sumCell('课槽被占·已拒排', s.slot_conflicts || 0, s.slot_conflicts ? 'is-bad' : 'is-ok');
    body.appendChild(sum);

    // ── 分组明细 ────────────────────────────────────────────────────
    body.appendChild(this._group('漏排', (d.missing || []).map(x => ({
      main: x.date + ' ' + x.day_label + ' ' + x.time,
      sub: x.student_name + ' · 模板 #' + x.fixed_lesson_id + '（因冲突被跳过或尚未物化, 解决冲突后自动补上）'
    })), 'ok'));

    // v0.17.59 — 固定课被「一个课槽只能有一节课」拒绝生成的明细。
    //   文案直接用后端给的 message (同一句 conflict 文案), 不在前端二次拼装 ——
    //   后端那句已经写清了「哪天、哪一段、被哪节课(#id, 时间, 教室)占了」和该怎么做。
    body.appendChild(this._group('课槽已有课 · 未排上', (d.slot_conflicts || []).map(x => ({
      main: (x.dates || []).join('、') + ' ' + (x.day_label || '') + ' ' + x.time,
      sub: (x.student_name || '') + ' · 模板 #' + x.fixed_lesson_id + ' · ' + x.message,
      warn: true
    })), 'warn'));
    body.appendChild(this._group('因卡过期停排', (d.paused || []).map(x => ({
      main: x.day_label + ' ' + x.start_time.slice(0, 5) + '-' + x.end_time.slice(0, 5),
      sub: x.student_name + ' · 卡 #' + x.card_id + (x.card_expires_at ? ' 到期 ' + x.card_expires_at : '') + ' · ' + x.reason,
      warn: !x.resumable
    })), 'ok'));
    body.appendChild(this._group('被单次改过', (d.overridden || []).map(x => ({
      main: x.date + ' 实际 ' + x.actual_time,
      sub: x.student_name + ' · 模板时间 ' + x.template_time + (x.cancelled ? ' · 已取消这一次' : '')
    })), 'info'));
    body.appendChild(this._group('孤儿课次', (d.orphan || []).map(x => ({
      main: x.date + ' 课次 #' + x.lesson_id,
      sub: x.reason + ' · 模板 #' + x.fixed_lesson_id + '（课次仍在展示, 确认后可在回收站恢复模板或删除课次）'
    })), 'info'));

    // ── 底部: 一键修复 ──────────────────────────────────────────────
    const btns = document.createElement('div');
    btns.className = 'ZS-fxmodal-btns';
    const closeBtn = document.createElement('div');
    closeBtn.className = 'ZS-fxmodal-btn is-ghost';
    closeBtn.textContent = '关闭';
    closeBtn.addEventListener('click', close);
    btns.appendChild(closeBtn);
    const mkFix = (label, action, count, cls) => {
      if (!count) return;
      const b = document.createElement('div');
      b.className = 'ZS-fxmodal-btn ' + cls + (state.fixing === action ? ' is-busy' : '');
      b.textContent = state.fixing === action ? '处理中…' : label;
      b.addEventListener('click', async () => {
        state.fixing = action;
        render();
        try {
          await Api.fixFixedLessons(opts.schoolId, action);
          state.data = await Api.checkFixedLessons(opts.schoolId);
          state.errorMsg = '';
        } catch (e) {
          // v0.17.66 — 同上 (检查那处), 改前这里只有 `|| '网络错误'`
          //   一键补排 / 恢复停排失败时真实原因同样在 `e.response.errors[0].detail`,
          //   `e.message` 是请求层的 422 文案; 「网络错误」会让用户误判成断网去反复重试。
          //   兜底文案「网络错误」原文保留, 降为最后一档。
          state.errorMsg = '修复失败: ' + errReason(e, '网络错误');
        }
        state.fixing = '';
        render();
        if (opts.onRefresh) opts.onRefresh();
      });
      btns.appendChild(b);
    };
    mkFix('恢复因过期停排的 ' + (s.paused_resumable || 0) + ' 个', 'resume', s.paused_resumable || 0, 'is-ghost');
    mkFix('一键补排漏排的 ' + (s.missing || 0) + ' 次', 'backfill', s.missing || 0, 'is-primary');
    dlg.appendChild(btns);
  },
  _sumCell(label, n, cls) {
    return '<div class="ZS-fxcheck-cell ' + cls + '"><div class="ZS-fxcheck-n">' + n + '</div><div class="ZS-fxcheck-l">' + this._esc(label) + '</div></div>';
  },
  _group(title, items, tone) {
    const wrap = document.createElement('div');
    wrap.className = 'ZS-fxcheck-group is-' + tone;
    const h = document.createElement('div');
    h.className = 'ZS-fxcheck-gh';
    h.textContent = title + ' (' + items.length + ')';
    wrap.appendChild(h);
    if (!items.length) {
      const empty = document.createElement('div');
      empty.className = 'ZS-fxcheck-empty';
      empty.textContent = '无';
      wrap.appendChild(empty);
      return wrap;
    }
    const ul = document.createElement('div');
    ul.className = 'ZS-fxcheck-list';
    items.forEach(it => {
      const row = document.createElement('div');
      row.className = 'ZS-fxcheck-row' + (it.warn ? ' is-warn' : '');
      const m = document.createElement('div');
      m.className = 'ZS-fxcheck-m';
      m.textContent = it.main;
      const s = document.createElement('div');
      s.className = 'ZS-fxcheck-s';
      s.textContent = it.sub;
      row.appendChild(m);
      row.appendChild(s);
      ul.appendChild(row);
    });
    wrap.appendChild(ul);
    return wrap;
  },
  _esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[c]);
  }
};
export default ZSFXCheck;
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/FixedCheckModal', ZSFXCheck);